import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, MapPin, Building2 } from "lucide-react";

/**
 * Editor delle sedi di lavoro di un professionista.
 * Un utente può avere una sede singola oppure più sedi (poliambulatori, sedi distaccate).
 * Ogni sede = { id?, name, city, address }
 *
 * Props:
 *  - value: Location[]
 *  - onChange: (Location[]) => void
 *  - testidPrefix: string (default "loc")
 *  - required: bool (se la prima sede è obbligatoria)
 */
export default function LocationsEditor({ value, onChange, testidPrefix = "loc", required = false }) {
  const locations = value && value.length > 0 ? value : [{ name: "", city: "", address: "" }];
  const multi = locations.length > 1;

  const update = (i, k, v) => {
    const next = locations.map((loc, idx) => (idx === i ? { ...loc, [k]: v } : loc));
    onChange(next);
  };
  const addLocation = () => onChange([...locations, { name: "", city: "", address: "" }]);
  const removeLocation = (i) => {
    if (locations.length === 1) {
      onChange([{ name: "", city: "", address: "" }]);
    } else {
      onChange(locations.filter((_, idx) => idx !== i));
    }
  };
  const setMulti = (checked) => {
    if (checked && locations.length === 1) addLocation();
    else if (!checked && locations.length > 1) onChange([locations[0]]);
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center gap-2">
        <Checkbox id={`${testidPrefix}-multi`} checked={multi} onCheckedChange={setMulti}
                  data-testid={`${testidPrefix}-multi-checkbox`} />
        <label htmlFor={`${testidPrefix}-multi`} className="text-sm text-slate-700 cursor-pointer">
          Sono presente in <span className="font-semibold">più sedi</span>
          <span className="text-slate-500 font-normal"> (es. poliambulatori, sedi distaccate)</span>
        </label>
      </div>

      {locations.map((loc, i) => (
        <div key={i} data-testid={`${testidPrefix}-item-${i}`}
             className="p-3 md:p-4 rounded-xl border border-slate-200 bg-slate-50/60 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
              <MapPin className="h-3.5 w-3.5 text-[#1E3A8A]" />
              {locations.length === 1 ? "Sede di lavoro" : `Sede ${i + 1}`}
            </div>
            {locations.length > 1 && (
              <Button type="button" size="icon" variant="ghost"
                      onClick={() => removeLocation(i)}
                      data-testid={`${testidPrefix}-remove-${i}`}
                      className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">
                Nome struttura {required && i === 0 && <span className="text-red-600">*</span>}
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={loc.name || ""}
                       onChange={(e) => update(i, "name", e.target.value)}
                       placeholder="Es. Poliambulatorio San Carlo — Studio Dott. Rossi"
                       className="pl-9"
                       data-testid={`${testidPrefix}-name-${i}`}
                       required={required && i === 0} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">
                Città {required && i === 0 && <span className="text-red-600">*</span>}
              </Label>
              <Input value={loc.city || ""}
                     onChange={(e) => update(i, "city", e.target.value)}
                     placeholder="Es. Roma"
                     data-testid={`${testidPrefix}-city-${i}`}
                     required={required && i === 0} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Indirizzo</Label>
              <Input value={loc.address || ""}
                     onChange={(e) => update(i, "address", e.target.value)}
                     placeholder="Es. Via Marconi 12"
                     data-testid={`${testidPrefix}-address-${i}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Telefono struttura</Label>
              <Input value={loc.phone || ""}
                     onChange={(e) => update(i, "phone", e.target.value)}
                     placeholder="Es. +39 091 123 4567"
                     data-testid={`${testidPrefix}-phone-${i}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Email struttura</Label>
              <Input type="email" value={loc.email || ""}
                     onChange={(e) => update(i, "email", e.target.value)}
                     placeholder="Es. info@poliambulatorio.it"
                     data-testid={`${testidPrefix}-email-${i}`} />
            </div>
          </div>
        </div>
      ))}

      {multi && (
        <Button type="button" variant="outline" size="sm"
                onClick={addLocation}
                data-testid={`${testidPrefix}-add`}
                className="border-dashed border-slate-300 text-slate-700 hover:bg-slate-100">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi un'altra sede
        </Button>
      )}
    </div>
  );
}
